import React, { useState } from 'react';
import { getIn, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { KoobButton, KoobTooltip } from '@koob/ui';
import { formatDate } from '@koob/dates';
import { categoryOf } from '../../../Hotels/Hotel/Contracts/EditContract/seasons/seasonModel';
import { resolveDistribution } from '../../../../utils/distributionInheritance';
import DistributionFields from './DistributionFields';

const ALL_DAYS = 7;
// 2024-01-01 is a Monday: ISO weekday n is 2024-01-0n.
const weekdayName = (day, locale) =>
  new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2024, 0, day)),
  );

const periodDates = (t, startAt, endAt) =>
  t('periodDates', {
    start: formatDate(startAt, 'DD-MM-YYYY'),
    end: formatDate(endAt, 'DD-MM-YYYY'),
  });

/** Whether a room lists the seasons of several contracts. */
export const hasSeveralContracts = (seasons) =>
  new Set(seasons.map(({ contractId }) => contractId)).size > 1;

/**
 * A room × season's category colour, name, weekdays and offer state. With
 * `withDates`, the span of its `periods` tells apart the same season of two
 * contracts.
 */
export const SeasonHeader = ({ season, periods = [], withDates = false }) => {
  const { t, i18n } = useTranslation('toConnectionRequest');
  const sorted = [...periods].sort(
    (a, b) => new Date(a.startAt) - new Date(b.startAt),
  );
  const days =
    season.daysOfWeek?.length === ALL_DAYS
      ? t('distribution.allDays')
      : (season.daysOfWeek ?? [])
          .map((day) => weekdayName(day, i18n.language))
          .join(', ');

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span
        className={`h-2.5 w-2.5 flex-none rounded-full ${
          categoryOf(season).dot
        }`}
      />
      <span className="text-sm font-semibold text-gray-900">{season.name}</span>
      <span className="text-xs text-gray-500">{days}</span>
      {withDates && sorted.length > 0 && (
        <span className="text-xs text-gray-500">
          {periodDates(t, sorted[0].startAt, sorted[sorted.length - 1].endAt)}
        </span>
      )}
      {!season.offered && (
        <span className="text-xs text-gray-500">
          {t('distribution.notOffered')}
        </span>
      )}
    </div>
  );
};

export const PeriodDistribution = ({
  room,
  roomIndex,
  period,
  periodIndex,
  levels,
  disabled,
}) => {
  const { t } = useTranslation('toConnectionRequest');
  const dates = periodDates(t, period.startAt, period.endAt);

  const allocations = [...period.allAllocations].sort((a, b) =>
    a.organization.scope === b.organization.scope
      ? b.allotmentCount - a.allotmentCount
      : a.organization.scope.localeCompare(b.organization.scope),
  );

  return (
    <div className="flex flex-col gap-2 border-t border-gray-100 py-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-600">{dates}</span>
        <KoobTooltip
          position="bottom"
          tip={allocations.map((allocation) => (
            <p key={allocation.id}>
              {t('allocation', {
                name: allocation.organization.displayName,
                count: allocation.allotmentCount,
                totalCount: period.totalAllotmentsAvailable,
              })}
            </p>
          ))}
        >
          <i className="fa-regular fa-circle-info text-xs text-gray-400" />
        </KoobTooltip>
      </div>
      <DistributionFields
        name={`rooms.${roomIndex}.periods.${periodIndex}.own`}
        inherited={resolveDistribution(levels, period.freeSale)}
        disabled={disabled}
        freeSaleDisabled={!period.freeSale}
        freeSaleLocksAllotment
        label={`${room.name} · ${dates}`}
      />
    </div>
  );
};

// One room × season of the rooms modal (KD-3896): its values inherit the
// room's, and its periods inherit its own.
const SeasonDistribution = ({
  room,
  roomIndex,
  season,
  seasonIndex,
  roomLevels,
  canDisplayPeriod,
  withDates,
  disabled,
}) => {
  const { t } = useTranslation('toConnectionRequest');
  const { errors } = useFormikContext();
  const [expanded, setExpanded] = useState(false);

  const periods = room.periods
    .map((period, periodIndex) => ({ period, periodIndex }))
    .filter(
      ({ period }) =>
        period.seasonRoomId === season.id && canDisplayPeriod(period),
    );
  const hasError = periods.some(({ periodIndex }) =>
    getIn(errors, `rooms.${roomIndex}.periods.${periodIndex}.own`),
  );
  const levels = [season.distribution, ...roomLevels];
  const contractFreeSale = periods.some(({ period }) => period.freeSale);

  return (
    <div
      className={`rounded-md border border-gray-200 px-4 py-3 ${
        season.offered ? '' : 'bg-gray-50 opacity-60'
      }`}
      data-testid="season-distribution"
    >
      <SeasonHeader
        season={season}
        periods={periods.map(({ period }) => period)}
        withDates={withDates}
      />

      {season.offered && (
        <>
          <div className="mt-3">
            <DistributionFields
              name={`rooms.${roomIndex}.seasons.${seasonIndex}.distribution`}
              inherited={resolveDistribution(roomLevels, contractFreeSale)}
              disabled={disabled}
              freeSaleDisabled={!contractFreeSale}
              label={`${room.name} · ${season.name}`}
            />
          </div>

          {periods.length > 0 && (
            <div className="mt-2">
              <KoobButton
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => setExpanded((current) => !current)}
              >
                <i
                  className={`fa-solid ${
                    expanded || hasError ? 'fa-chevron-up' : 'fa-chevron-down'
                  }`}
                />
                {t('distribution.periods', { count: periods.length })}
              </KoobButton>
              {hasError && (
                <span className="ml-2 text-xs text-red-600">
                  {t('allotmentNotValid')}
                </span>
              )}

              {(expanded || hasError) &&
                periods.map(({ period, periodIndex }) => (
                  <PeriodDistribution
                    key={period.id}
                    room={room}
                    roomIndex={roomIndex}
                    period={period}
                    periodIndex={periodIndex}
                    levels={levels}
                    disabled={disabled}
                  />
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SeasonDistribution;
